import {
  DeleteTwoTone,
  ExportOutlined,
  PlusSquareFilled,
} from "@ant-design/icons";
import {
  type AuthFetch,
  useNavigateWithSearchParams,
} from "@p0security/frontend-core";
import { itemRouteSegment } from "@p0security/shared/install/routes";
import type {
  ConfigOf,
  InstallSpec,
  ItemConfigOf,
} from "@p0security/shared/install/types";
import type { IntegrationStatus } from "@p0security/shared/integrations/shared";
import type { ColumnsType } from "@p0security/ui";
import {
  Button,
  Flex,
  Input,
  Spacing,
  Spin,
  Table,
  Typography,
} from "@p0security/ui";
import { sortBy, upperFirst } from "lodash";
import pluralize from "pluralize";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import type {
  FrontendInstallContext,
  FrontendInstaller,
  InstallSiblings,
} from "../types";
import { Config, configPath } from "./Config";
import type { InstallProps } from "./Install";
import { New } from "./New";
import { Singleton } from "./Singleton";
import { evaluate } from "./util.ts";

export type ComponentProps<T extends InstallSpec> = Omit<
  InstallProps<T>,
  "installer"
> & {
  authFetch: AuthFetch;
  canEdit: boolean;
  config: ConfigOf<T>;
  context: FrontendInstallContext<ConfigOf<T>>;
  componentKey: string;
  docsUrl?: string;
  error: string | undefined;
  installer: FrontendInstaller<T>["components"];
  isFetching: boolean;
  /** Surfaces a non-blocking warning returned by a configure/verify step */
  onWarning: (warning: string | undefined) => void;
};

export const NEW_SENTINEL = "_new";

const INCOMPLETE_STATE_PRIORITY: Record<IntegrationStatus, number> = {
  error: 1,
  stage: 2,
  configure: 3,
  installed: 4,
};

const DeleteAction: React.FC<{
  id: string;
  deleteItem: (id: string) => Promise<void>;
  DeleteModal: InstallSiblings["DeleteModal"];
}> = ({ id, deleteItem, DeleteModal }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const toggleDeleteModal = useCallback(
    () => setIsDeleteModalOpen(!isDeleteModalOpen),
    [isDeleteModalOpen]
  );
  const confirmDelete = useCallback(async () => {
    setIsDeleting(true);
    setIsDeleteModalOpen(false);
    await deleteItem(id);
    setIsDeleting(false);
  }, [deleteItem, id]);

  return (
    <>
      <Button
        type="link"
        icon={<DeleteTwoTone />}
        loading={isDeleting}
        onClick={isDeleting ? undefined : toggleDeleteModal}
      />
      <DeleteModal
        id={id}
        isOpen={isDeleteModalOpen}
        toggleOpen={toggleDeleteModal}
        onRemove={confirmDelete}
      />
    </>
  );
};

const itemColumns = (props: {
  title: string;
  canEdit: boolean;
  deleteItem: (item: string) => Promise<void>;
  DeleteModal: InstallSiblings["DeleteModal"];
}): ColumnsType<ItemConfigOf<any> & { key: string }> => [
  {
    key: "id",
    title: props.title,
    render: (_, { key, label }) => (
      <Link to={itemRouteSegment(key)} relative="path">
        {label ? `${label} (${key})` : key}
      </Link>
    ),
  },
  {
    key: "state",
    title: "State",
    // TODO: make pretty
    render: (_, { state }) =>
      state === "installed" ? "Installed" : "In progress",
  },
  {
    key: "actions",
    title: "",
    render: (_, { key }) =>
      props.canEdit && (
        <DeleteAction
          id={key}
          deleteItem={props.deleteItem}
          DeleteModal={props.DeleteModal}
        />
      ),
  },
];

export const Component = <T extends InstallSpec>(props: ComponentProps<T>) => {
  // React Router has already decoded the param, so use it as is. Links to
  // this page must build it with `itemRouteSegment`, which survives React
  // Router 6.10 decoding the path twice.
  const { id } = useParams<{
    id: string;
  }>();
  const [filter, setFilter] = useState("");
  const navigate = useNavigateWithSearchParams();
  const {
    authFetch,
    canEdit,
    componentKey,
    config,
    context,
    docsUrl,
    registration,
    siblings,
  } = props;
  const component = registration.components?.[componentKey];
  const items = config?.[componentKey];

  const isSingleton = component?.type === "singleton";

  const deleteItem = useCallback(
    async (id: string) => {
      await authFetch(configPath({ ...props, id }), { method: "DELETE" });
    },
    [authFetch, props]
  );

  const updateFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setFilter(event.target.value),
    []
  );

  const itemKind = useMemo(
    () => evaluate(registration.itemKind, componentKey),
    [componentKey, registration.itemKind]
  );

  const columns = useMemo(
    () =>
      itemColumns({
        title: upperFirst(itemKind),
        canEdit,
        deleteItem,
        DeleteModal: siblings.DeleteModal,
      }),
    [canEdit, deleteItem, itemKind, siblings.DeleteModal]
  );

  const data = useMemo(
    () =>
      sortBy(
        Object.entries(items ?? {})
          .map(([key, item]) => ({
            ...item,
            key,
          }))
          .filter(
            ({ key, label }) =>
              !filter ||
              key.toLowerCase().includes(filter) ||
              label?.toLowerCase().includes(filter)
          ),
        [({ state }) => INCOMPLETE_STATE_PRIORITY[state], "key"]
      ),
    [filter, items]
  );

  const item = useMemo(
    () => (id !== undefined && id !== NEW_SENTINEL ? items?.[id] : undefined),
    [items, id]
  );

  const installNew = useCallback(
    () => navigate("_new", { relative: "path" }),
    [navigate]
  );

  return (
    <>
      <Spacing asChild mb={16}>
        <Flex justify="space-between" align="flex-start">
          <div>
            <Spacing asChild mb={8}>
              <Typography.Title level={3}>{component?.label}</Typography.Title>
            </Spacing>
            <Spacing asChild mb={0}>
              <Typography.Paragraph>
                {component?.description}
              </Typography.Paragraph>
            </Spacing>
          </div>
          {docsUrl && (
            <Button
              type="link"
              size="small"
              className="button-link"
              href={docsUrl}
              target="_blank"
              rel="noreferrer"
              icon={<ExportOutlined />}
            >
              View Documentation
            </Button>
          )}
        </Flex>
      </Spacing>
      {isSingleton ? (
        <Singleton {...props} component={component} />
      ) : id === NEW_SENTINEL ? (
        <New {...props} component={component} context={context} />
      ) : id ? (
        item ? (
          <Config
            {...props}
            component={component}
            context={context}
            id={id}
            item={item}
          />
        ) : (
          <Spin />
        )
      ) : (
        <>
          <Typography.Title level={4}>
            Installed {pluralize(itemKind)}
          </Typography.Title>
          <Spacing asChild mb={16}>
            <Flex className="w-full" gap="1em" justify="space-between">
              <Input
                placeholder={`Filter ${pluralize(itemKind)}...`}
                value={filter}
                onChange={updateFilter}
                /* eslint-disable-line react/forbid-component-props */ style={{
                  maxWidth: "25em",
                }}
              />
              {!canEdit ? null : !component.maxItems ||
                component.maxItems > data.length ? (
                <Button
                  onClick={installNew}
                  type="primary"
                  icon={<PlusSquareFilled />}
                >
                  Add {itemKind}
                </Button>
              ) : (
                `A maximum of ${component.maxItems} ${pluralize(
                  itemKind,
                  component.maxItems
                )} may be installed`
              )}
            </Flex>
          </Spacing>
          <Table<ItemConfigOf<any> & { key: string }>
            columns={columns}
            dataSource={data}
            pagination={{ hideOnSinglePage: true }}
          />
        </>
      )}
    </>
  );
};
